import React from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  useTheme,
  Container,
  alpha,
} from "@mui/material";
import {
  SecurityOutlined,
  ArrowBack,
  EmailOutlined,
} from "@mui/icons-material";
import AppButton from "../components/buttons/AppButton";

interface UnauthorizedPageTheme {
  palette: {
    mode: "dark" | "light";
    primary: {
      main: string;
    };
    secondary: {
      main: string;
    };
    neutral: {
      light: string;
      main: string;
    };
    text: {
      primary: string;
      secondary: string;
    };
    divider: string;
  };
  appRadius: {
    md: string;
    lg: string;
    xl: string;
  };
  typography: {
    button: {
      fontSize: string | number;
    };
  };
}

const UnauthorizedPage: React.FC = () => {
  const theme = useTheme() as unknown as UnauthorizedPageTheme;

  const handleGoBack = (): void => {
    window.history.back();
  };

  const handleContactSupport = (): void => {
    window.location.href = "mailto:customersupport@deitiestech.com";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? theme.palette.primary.main
            : `linear-gradient(135deg, ${theme.palette.neutral.light} 0%, #ffffff 100%)`,
        p: 2,
      }}
    >
      {/* Decorative background blobs */}
      <Box
        sx={{
          position: "absolute",
          top: "5%",
          left: "5%",
          width: 100,
          height: 100,
          borderRadius: "50%",
          bgcolor: theme.palette.primary.main,
          opacity: 0.1,
          filter: "blur(40px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          right: "5%",
          width: 150,
          height: 150,
          borderRadius: "50%",
          bgcolor: theme.palette.secondary.main,
          opacity: 0.1,
          filter: "blur(60px)",
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={theme.palette.mode === "dark" ? 4 : 10}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: "center",
            borderRadius: theme.appRadius.xl,
            bgcolor: "background.paper",
            border:
              theme.palette.mode === "dark"
                ? `1px solid ${theme.palette.divider}`
                : "none",
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <SecurityOutlined
              sx={{ fontSize: 40, color: theme.palette.primary.main }}
            />
          </Box>

          {/* Status Pill */}
          <Box sx={{ mb: 3 }}>
            <Chip
              label="401 Unauthorized"
              color="primary"
              size="medium"
              sx={{
                fontWeight: 600,
                borderRadius: theme.appRadius.lg,
                height: 32,
              }}
            />
          </Box>

          {/* Heading */}
          <Typography
            variant="h3"
            gutterBottom
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            Access Restricted
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", mb: 4, lineHeight: 1.6 }}
          >
            You do not have permission to access this resource. Please contact
            your administrator or try logging in with the appropriate
            credentials.
          </Typography>

          {/* Actions */}
          <Stack spacing={2}>
            <AppButton
              variant="gradient"
              color="primary"
              fullWidth
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              sx={{
                py: 1.5,
                borderRadius: theme.appRadius.md,
                fontSize: theme.typography.button.fontSize,
              }}
            >
              Go Back
            </AppButton>

            <AppButton
              variant="contained"
              fullWidth
              startIcon={<EmailOutlined />}
              onClick={handleContactSupport}
              sx={{
                py: 1.5,
                borderRadius: theme.appRadius.md,
                bgcolor: theme.palette.neutral.light,
                color: theme.palette.text.primary,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: theme.palette.neutral.main,
                  boxShadow: "none",
                },
              }}
            >
              Contact Support
            </AppButton>
          </Stack>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Need help? Contact{" "}
            <Typography
              component="span"
              variant="body2"
              onClick={handleContactSupport}
              sx={{
                color: "primary.main",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              customersupport@fijiplatforms.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UnauthorizedPage;
